import { useApolloClient } from "@apollo/client";
import { DataGrid, Modal, ModalOpenButton } from "@msys/ui";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  GroupAdd as GroupAddIcon,
} from "@mui/icons-material";
import {
  Alert,
  Button,
  ButtonProps,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { FieldArray, Form, Formik, useFormikContext } from "formik";
import { uniqueId } from "lodash-es";
import { useSnackbar } from "notistack";
import React, { useMemo } from "react";
import * as Yup from "yup";
import { RadioGroupField } from "../../../../commons/form-fields/RadioGroupField.js";
import { RESULTS_PER_PAGE_OPTIONS } from "../../../../constants.js";
import { CraftsmenSelectModal } from "../../../craftsman/CraftsmenSelectModal.js";
import { OrganisationAvatar } from "../../../organisations/OrganisationAvatar.js";
import { OrganisationAvatarFragment } from "../../../organisations/OrganisationAvatar.generated.js";
import {
  QuoteTemplateShareButton_QuoteTemplateFragment,
  useShareQuoteTemplatePubliclyMutation,
  useShareQuoteTemplateWithOrganisationsMutation,
} from "./QuoteTemplateShareButton.generated.js";
import { PaginationModel } from "../../../../commons/pagination/usePaginationParams.js";
import { GridColDef } from "@mui/x-data-grid-premium";
import { useDataGridStateStore } from "../../../users/useDataGridStateStore.js";

interface Props extends ButtonProps {
  doc: QuoteTemplateShareButton_QuoteTemplateFragment;
  buttonProps?: ButtonProps;
  buttonTitle?: string;
  versionNumber?: number;
  refetchQueries?: string[];
}

export const QuoteTemplateShareButton = ({
  doc,
  buttonProps,
  buttonTitle,
  versionNumber,
  refetchQueries,
}: Props) => {
  const { t } = useTranslate(["TemplatesSharing", "Global"]);

  return doc.hasAnyPublishedVersion && !doc.templateSharing?.sharedPublicly ? (
    <ModalOpenButton
      Modal={QuoteTemplatesShareModal}
      modalProps={{
        template: doc,
        templates: [doc],
        versionNumber,
        refetchQueries,
      }}
    >
      <Button
        startIcon={<GroupAddIcon />}
        variant="contained"
        color="secondary"
        {...buttonProps}
      >
        {buttonTitle ?? t("Share", { ns: "TemplatesSharing" })}
      </Button>
    </ModalOpenButton>
  ) : null;
};

type ShareType = "public" | "protected";

interface FormValues {
  type: ShareType;
  companies: OrganisationAvatarFragment[];
}

export const QuoteTemplatesShareModal = ({
  template,
  templates,
  handleComplete,
  handleClose,
  versionNumber,
  refetchQueries,
}: {
  template?: QuoteTemplateShareButton_QuoteTemplateFragment;
  templates: QuoteTemplateShareButton_QuoteTemplateFragment[];
  handleComplete?: (updatedItemCount: number) => void;
  handleClose: () => void;
  versionNumber?: number;
  refetchQueries?: string[];
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslate(["TemplatesSharing", "Global"]);

  const client = useApolloClient();

  const [shareWithOrganisations] =
    useShareQuoteTemplateWithOrganisationsMutation({
      client,
      refetchQueries,
      awaitRefetchQueries: true,
    });
  const [sharePublicly] = useShareQuoteTemplatePubliclyMutation({
    client,
    refetchQueries,
    awaitRefetchQueries: true,
  });

  const formId = useMemo(() => uniqueId(), []);

  const [paginationModel, setPaginationModel] = React.useState<PaginationModel>(
    { page: 0, pageSize: 10 }
  );

  const initialValues: FormValues = {
    type: "protected",
    companies: [], //doc.sharedWithOrganisations, // only upsert is allowed!
  };

  const validationSchema = React.useMemo(
    () =>
      Yup.object().shape({
        type: Yup.string().required(),
        companies: Yup.array()
          .test(
            "notEmptyInCaseOfProtected",
            t("Should not be empty", {
              ns: "TemplatesSharing",
            }),
            function (companies) {
              const type = this.resolve(Yup.ref("type")) as ShareType;
              if (
                type === "protected" &&
                (!companies || companies.length === 0)
              )
                return false;
              return true;
            }
          )
          .required(),
      }),
    [t]
  );

  const handleSubmit = async (values: FormValues) => {
    const sharedWithSystemOrganisationIds =
      values.type === "protected"
        ? values.companies.map(company => company.id)
        : [];

    try {
      await Promise.all(
        templates.map(async template => {
          if (values.type === "public") {
            await sharePublicly({
              variables: {
                input: {
                  quoteTemplateId: template.id,
                  versionNumber,
                },
              },
            });
          } else if (values.type === "protected") {
            await shareWithOrganisations({
              variables: {
                input: {
                  quoteTemplateId: template.id,
                  sharedWithSystemOrganisationIds,
                  versionNumber,
                },
              },
            });
          } else {
            throw new Error("Invalid sharing type");
          }
        })
      );
      enqueueSnackbar(
        t("Sharing settings updated", { ns: "TemplatesSharing" })
      );
      handleComplete?.(templates.length);
      handleClose();
    } catch (e) {
      if (e instanceof Error) enqueueSnackbar(e.message, { variant: "error" });
    }
  };

  const sharedWithOrgs =
    template?.templateSharing?.sharedWithOrganisations ?? [];

  const columns = React.useMemo(
    (): GridColDef<OrganisationAvatarFragment & { persisted?: boolean }>[] => [
      {
        field: "companyName",
        headerName: t("Craftsman company", {
          ns: "TemplatesSharing",
        }),
        flex: 1,
        minWidth: 100,
        sortable: false,
        renderCell: ({ row: item }) => (
          <Stack alignItems="center" direction="row" spacing={1}>
            <OrganisationAvatar size="s" organisationAvatar={item} />
            <Typography>{item.title}</Typography>
          </Stack>
        ),
      },
      {
        field: "actions",
        headerName: "",
        flex: 0.1,
        minWidth: 50,
        sortable: false,
        renderCell: ({ row: item }) => {
          if (item.persisted) return null;
          return <RemoveCompanyIconButton orgId={item.id} />;
        },
      },
    ],
    [t]
  );

  const stateStore = useDataGridStateStore("QuoteTemplatesShareModal");

  return (
    <Formik<FormValues>
      validationSchema={validationSchema}
      validateOnMount
      onSubmit={handleSubmit}
      initialValues={initialValues}
    >
      {({ isSubmitting, dirty, isValid, values }) => (
        <Modal
          title={t("Sharing settings", {
            ns: "TemplatesSharing",
          })}
          dialogProps={{ maxWidth: "sm" }}
          handleClose={handleClose}
          actionButtons={[
            {
              label: t("Close", {
                ns: "Global",
              }),
              handleClick: handleClose,
              buttonProps: { variant: "text" },
            },
            {
              label: t("Save", {
                ns: "Global",
              }),
              buttonProps: {
                loading: isSubmitting,
                form: formId,
                type: "submit",
                disabled: !dirty || !isValid,
              },
            },
          ]}
        >
          <Form id={formId}>
            <Stack direction="column" spacing={2}>
              <Alert severity="warning" variant="outlined">
                {t(
                  "Once you share the template with an organisation or public, you can’t unshare it",
                  { ns: "TemplatesSharing" }
                )}
              </Alert>
              <RadioGroupField
                name="type"
                options={[
                  {
                    label: t("Public", {
                      ns: "TemplatesSharing",
                    }),
                    subLabel: t("Available to all organisations", {
                      ns: "TemplatesSharing",
                    }),
                    value: "public",
                  },
                  {
                    label: t("Protected", {
                      ns: "TemplatesSharing",
                    }),
                    subLabel: t("Available to selected organisations", {
                      ns: "TemplatesSharing",
                    }),
                    value: "protected",
                  },
                ]}
              />
              {values.type === "protected" && (
                <FieldArray
                  name="companies"
                  render={arrayHelpers => (
                    <>
                      {sharedWithOrgs.length + values.companies.length > 0 && (
                        <DataGrid<
                          OrganisationAvatarFragment & { persisted?: boolean }
                        >
                          stateStore={stateStore}
                          sx={{ border: "none" }}
                          density="standard"
                          hideFooter={
                            sharedWithOrgs.length + values.companies.length ===
                            0
                          }
                          columns={columns}
                          rows={[
                            ...sharedWithOrgs.map(org => ({
                              ...org,
                              persisted: true,
                            })),
                            ...values.companies,
                          ]}
                          paginationMode="client"
                          paginationModel={paginationModel}
                          onPaginationModelChange={newPaginationModel => {
                            setPaginationModel(newPaginationModel);
                          }}
                          pageSizeOptions={RESULTS_PER_PAGE_OPTIONS}
                        />
                      )}
                      {values.companies.length === 0 && (
                        <Stack direction="row" justifyContent="center" py={1}>
                          <Typography
                            variant="caption"
                            align="center"
                            sx={{ color: theme => theme.palette.grey[400] }}
                          >
                            {t("You have not added any companies yet.", {
                              ns: "TemplatesSharing",
                            })}
                          </Typography>
                        </Stack>
                      )}
                      <Stack direction="row" justifyContent="center">
                        <ModalOpenButton
                          Modal={CraftsmenSelectModal}
                          modalProps={{
                            loading: isSubmitting,
                            handleComplete: organisations => {
                              for (const organisation of organisations)
                                arrayHelpers.push(organisation);
                            },
                            title: t("Add companies", {
                              ns: "TemplatesSharing",
                            }),
                            omitOrganisationIds: [
                              ...sharedWithOrgs,
                              ...values.companies,
                            ].map(c => c.id),
                          }}
                        >
                          <Button
                            color="secondary"
                            variant="text"
                            startIcon={<AddIcon />}
                          >
                            {t("Add companies", {
                              ns: "TemplatesSharing",
                            })}
                          </Button>
                        </ModalOpenButton>
                      </Stack>
                    </>
                  )}
                />
              )}
            </Stack>
          </Form>
        </Modal>
      )}
    </Formik>
  );
};

function RemoveCompanyIconButton({ orgId }: { orgId: string }) {
  const formik = useFormikContext<FormValues>();
  return (
    <IconButton
      onClick={e => {
        e.preventDefault();
        e.stopPropagation();
        formik.setFieldValue(
          "companies",
          formik.values.companies.filter(c => c.id !== orgId)
        );
      }}
      size="small"
      color="primary"
    >
      <DeleteIcon />
    </IconButton>
  );
}
